import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { cueClose, cueEdit, cueQrCode, cueStart, cueTarget, cueWarning, IconComponent } from '@cue/ui/icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { translation } from '@cue/translations';
import { PathInfoResultEnum, PathResultCrate, StartTargetResultEnum } from '@cue/navigation-services';
import { NavigationStateEnum, RouteStateEnum } from '@cue/data';
import { ResponsiveService } from '@cue/responsivity';

@Component({
  selector: 'cue-start-target-basic-info',
  templateUrl: './start-target-basic-info.component.html',
  styleUrls: ['./start-target-basic-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe, IconComponent],
})
export class StartTargetBasicInfoComponent {
  protected startTargetResultEnum = StartTargetResultEnum;
  protected ICONS = {
    cueTarget,
    cueStart,
    cueEdit,
    cueWarning,
    cueClose,
    cueQrCode,
  };
  protected readonly navigationStateEnum = NavigationStateEnum;
  protected readonly translation = translation;

  responsiveService = inject(ResponsiveService);
  targetName = input<string>();
  startName = input<string>();
  isKioskMode = input<boolean>();
  isMobileMode = input<boolean>();
  currentRouteState = input<RouteStateEnum>();
  routeSearchResult = input<PathResultCrate>();
  canShowRoute = input<boolean>();

  protected routeUiState = computed(() => {
    const currentRouteState = this.currentRouteState();
    const routeSearchResult = this.routeSearchResult();

    if (currentRouteState === RouteStateEnum.StartNotFound) {
      return {
        startError: true,
        targetError: false,
        routeError: false,
        message: translation.wayfinding.startNotFound,
      };
    }

    if (currentRouteState === RouteStateEnum.TargetNotFound) {
      return {
        startError: false,
        targetError: true,
        routeError: false,
        message: translation.wayfinding.targetNotFound,
      };
    }

    const startRequested = routeSearchResult?.start !== StartTargetResultEnum.NotRequested;
    const targetRequested = routeSearchResult?.target !== StartTargetResultEnum.NotRequested;
    const sameStartTarget =
      currentRouteState === RouteStateEnum.SameStartTarget ||
      (routeSearchResult?.path === PathInfoResultEnum.SameStartAsTarget && startRequested && targetRequested);

    if (sameStartTarget) {
      return {
        startError: true,
        targetError: true,
        routeError: false,
        message: translation.wayfinding.sameStartAsTarget,
      };
    }

    if (routeSearchResult == null) {
      return {
        startError: false,
        targetError: false,
        routeError: false,
        message: '',
      };
    }

    if (
      routeSearchResult.target === StartTargetResultEnum.NotFound &&
      routeSearchResult.start === StartTargetResultEnum.NotFound
    ) {
      return {
        startError: true,
        targetError: true,
        routeError: false,
        message: translation.wayfinding.startAndTargetNotFound,
      };
    }

    if (routeSearchResult.target === StartTargetResultEnum.NotFound) {
      return {
        startError: false,
        targetError: true,
        routeError: false,
        message: translation.wayfinding.targetNotFound,
      };
    }

    if (routeSearchResult.start === StartTargetResultEnum.NotFound) {
      return {
        startError: true,
        targetError: false,
        routeError: false,
        message: translation.wayfinding.startNotFound,
      };
    }

    if (routeSearchResult.path === PathInfoResultEnum.FoundOnlyNotBarrierPath) {
      return {
        startError: false,
        targetError: false,
        routeError: false,
        message: translation.wayfinding.barrierFreenotFound,
      };
    }

    if (routeSearchResult.path === PathInfoResultEnum.PathNotFound && startRequested && targetRequested) {
      return {
        startError: false,
        targetError: false,
        routeError: true,
        message: translation.wayfinding.notFound,
      };
    }

    return {
      startError: false,
      targetError: false,
      routeError: false,
      message: '',
    };
  });

  protected startInputError = computed(() => this.routeUiState().startError);
  protected targetInputError = computed(() => this.routeUiState().targetError);
  protected routeConnectorError = computed(() => this.routeUiState().routeError);
  protected routeErrorMessage = computed(() => this.routeUiState().message);

  protected highlightStartSelection = computed(() => {
    if (this.startInputError()) {
      return false;
    }

    if (this.routeSearchResult() != null) {
      return this.routeSearchResult()?.start !== StartTargetResultEnum.Found;
    }

    if (this.startName() === '' && this.targetName() === '') {
      return true;
    }

    return this.startName() === '' && this.targetName() !== '';
  });

  protected highlightTargetSelection = computed(() => {
    if (this.targetInputError()) {
      return false;
    }

    if (this.routeSearchResult() != null) {
      return this.routeSearchResult()?.target !== StartTargetResultEnum.Found;
    }

    if (this.startName() === '' && this.targetName() === '') {
      return true;
    }

    return this.targetName() === '' && this.startName() !== '';
  });

  scanStartSelection = output();
  wantChangeStartOrTarget = output<NavigationStateEnum>();

  wantDeleteStart = output<void>();
  wantDeleteTarget = output<void>();

  protected deleteStart(mouseEvent: MouseEvent) {
    this.wantDeleteStart.emit();
    mouseEvent.stopPropagation();
  }

  protected deleteTarget(mouseEvent: MouseEvent) {
    this.wantDeleteTarget.emit();
    mouseEvent.stopPropagation();
  }
}
